import React from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import map from 'lodash/map';
import uniqBy from 'lodash/uniqBy';

import Select from 'zignal-landscape/lib/Select';
import styles from './TextSelect.css';

const SELECT_PLACEHOLDER = 'Select a value';

function _formatValuesAsSelect2Data(values) {
  return values.map(({ _id, name, ...other}) => {
    // Select needs `id` and `text` property on each value
    return {
      value: _id,
      label: name,
      ...other
    };
  });
}

function _formatSelect2DataAsValues(data) {
  return data.map(({ label, value, ...other}) => {
    // Our backend serves `_id` and needs `name` property on each value
    return {
      _id: value,
      name: label,
      ...other
    };
  });
}

class TextSelect extends React.Component {
  constructor(props) {
    super(props);

    const { initiallyAppliedValues } = props;

    this.onChange = this.onChange.bind(this);
    this.onInputKeyDown = this.onInputKeyDown.bind(this);
    this.renderDisplayContent = this.renderDisplayContent.bind(this);
    this.renderEditContent = this.renderEditContent.bind(this);
    this.onMenuOpen = this.onMenuOpen.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);

    this.state = {
      counter: 0,
      isLoading: false,
      message: null,
      selectedValues: initiallyAppliedValues,
      menuOpen: false
    };
  }

  componentDidUpdate() {
    const { isOpen } = this.props;
    if (isOpen) {
      const thisEl = findDOMNode(this);
      // We no longer can add a ref to the Select component. So, we go this route.
      thisEl.getElementsByTagName('input')[0].focus();
    }
  }

  onChange(newData) {
    const {
      onTextChanged
    } = this.props;
    const formattedNewValues = _formatSelect2DataAsValues(newData);
    const newState = {
      message: null,
      selectedValues: formattedNewValues
    };

    this.setState(newState);
    onTextChanged(formattedNewValues);
  }

  onInputKeyDown(e) {
    const { focusNextField, focusPreviousField } = this.props;
    const { menuOpen } = this.state;
    if(e.key === 'ArrowDown' && !menuOpen) {
      e.preventDefault();
      e.stopPropagation();
      focusNextField();
    }

    if(e.key === 'ArrowUp' && !menuOpen) {
      e.preventDefault();
      e.stopPropagation();
      focusPreviousField();
    }
  }

  onMenuOpen() {
    this.setState({menuOpen: true});
  }

  onMenuClose() {
    this.setState({menuOpen: false});
  }

  renderEditContent() {
    const {
      field,
      initiallyAppliedValues
    } = this.props;
    const {
      selectedValues
    } = this.state;


    // After user creates new values, `initiallyAppliedValues` may hold new values
    // not found in `field.values`, so they must be combined to form total set
    // of possible values.

    const combinedValues = field.values.concat(initiallyAppliedValues);
    const possibleValues = uniqBy(combinedValues, '_id');

    return (
      <div>
        <Select
          allowCreate
          className={styles.textField}
          isMulti
          showArrow={false}
          onChange={this.onChange}
          value={_formatValuesAsSelect2Data(selectedValues)}
          options={_formatValuesAsSelect2Data(possibleValues)}
          onInputKeyDown={this.onInputKeyDown}
          onOpen={this.onMenuOpen}
          onClose={this.onMenuClose}
        />
      </div>
    );
  }

  renderDisplayContent() {
    const { selectedValues } = this.state;
    const valueNames = map(selectedValues, 'name');
    const hasValues = (valueNames.length !== 0);
    return (
      <p className={hasValues ? styles.value : styles.select}>
        {hasValues ? valueNames.join(', ') : SELECT_PLACEHOLDER}
      </p>
    );
  }

  render() {
    const { isOpen } = this.props;
    if(isOpen) {
      return this.renderEditContent();
    }
    return this.renderDisplayContent();
  }
}


TextSelect.propTypes = {
  campaignId: PropTypes.string.isRequired,
  field: PropTypes.object.isRequired,
  initiallyAppliedValues: PropTypes.array,
  isOpen: PropTypes.bool,
  onTextChanged: PropTypes.func.isRequired,
  onSelectedIndexChanged: PropTypes.func,
  selectedIndex: PropTypes.number,
  focusNextField: PropTypes.func,
  focusPreviousField: PropTypes.func
};

TextSelect.defaultProps = {
  initiallyAppliedValues: [],
  isOpen: false,
};

export default TextSelect;
